import { Badge } from '@awpmarine/ui/components/ui/badge';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbSeparator,
} from '@awpmarine/ui/components/ui/breadcrumb';
import { Link, createFileRoute, notFound } from '@tanstack/react-router';
import { ExternalLinkIcon } from 'lucide-react';

import { ContentLanguageNotice, contentLang } from '@/components/content-language';
import { MarkdownDocs } from '@/components/markdown';
import { NewsCard, newsItems } from '@/components/news';
import { formatDate } from '@/lib/dates';
import { pageHead } from '@/lib/head';
import { ogImage } from '@/lib/open-graph';
import { isTranslated, news, translatedLocales } from '@/lib/source';
import { m } from '@/paraglide/messages';
import { getLocale } from '@/paraglide/runtime';
import { config } from '@/site-config';

export const Route = createFileRoute('/news/$')({
  // No server function: this runs in the browser on client-side navigation too (ADR 015).
  loader: async ({ params }) => {
    const post = news.getPage(params._splat?.split('/') ?? [], getLocale());
    if (!post) throw notFound();
    await post.data.preload();
    const image = ogImage(post.url);

    return {
      title: post.data.title,
      description: post.data.description ?? '',
      url: post.url,
      translated: translatedLocales(news, post.slugs),
      publishedTime: post.data.date,
      image,
      articleLd: JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'NewsArticle',
        'headline': post.data.title,
        'description': post.data.description,
        'datePublished': post.data.date,
        'image': [new URL(image, config.siteUrl).href],
        'mainEntityOfPage': new URL(post.url, config.siteUrl).href,
        'author': { '@type': 'Organization', 'name': config.name },
        'publisher': {
          '@type': 'Organization',
          'name': config.name,
          'logo': { '@type': 'ImageObject', 'url': `${config.siteUrl}/logo.svg` },
        },
      }),
    };
  },
  head: ({ loaderData }) =>
    pageHead({
      title: loaderData?.title ?? m.news_title(),
      description: loaderData?.description ?? '',
      canonical: loaderData?.url ?? '/news',
      image: loaderData?.image,
      type: 'article',
      translated: loaderData?.translated,
      meta: loaderData ? [{ property: 'article:published_time', content: loaderData.publishedTime }] : [],
      breadcrumbs: loaderData && [
        { name: m.article_breadcrumb(), path: '/news' },
        { name: loaderData.title, path: loaderData.url },
      ],
      // JSON-LD structured data: a static, code-controlled payload with no user input.
      scripts: loaderData ? [{ type: 'application/ld+json', children: loaderData.articleLd }] : [],
    }),
  component: NewsArticlePage,
});

function NewsArticlePage() {
  const { _splat } = Route.useParams();
  const post = news.getPage(_splat?.split('/') ?? [], getLocale());
  if (!post) throw notFound();
  const translated = isTranslated(post);

  const { title, description, source, tags, body } = post.data;
  const tag = tags?.[0];
  const more = newsItems()
    .filter((item) => item.slug !== post.slugs.join('/'))
    .slice(0, 3);

  return (
    <>
      <section className='bg-mist'>
        <div className='mx-auto max-w-210 px-5 py-12 sm:px-8 sm:pt-16 sm:pb-14'>
          <Breadcrumb className='mb-6'>
            <BreadcrumbList className='gap-2 text-base sm:gap-2'>
              <BreadcrumbItem>
                <BreadcrumbLink render={<Link to='/news' />} className='hover:text-brand'>
                  {m.article_breadcrumb()}
                </BreadcrumbLink>
              </BreadcrumbItem>
              {tag && (
                <>
                  <BreadcrumbSeparator>/</BreadcrumbSeparator>
                  <BreadcrumbItem>{tag}</BreadcrumbItem>
                </>
              )}
            </BreadcrumbList>
          </Breadcrumb>
          <h1
            lang={contentLang(translated)}
            className='mb-5 text-[clamp(34px,4vw,50px)] leading-[1.1] font-bold tracking-[-0.02em]'
          >
            {title}
          </h1>
          <div className='text-subtle flex flex-wrap items-center gap-x-4 gap-y-2.5'>
            <time dateTime={post.data.date}>{formatDate(post.data.date)}</time>
            {source && (
              <>
                <span aria-hidden>·</span>
                <span>{m.news_source({ source: source.name })}</span>
              </>
            )}
            {tag && (
              <Badge className='h-auto rounded-full bg-[#eef3fa] px-2.5 py-1 text-[13px] font-semibold text-[#1f4a80]'>
                {tag}
              </Badge>
            )}
          </div>
          <ContentLanguageNotice translated={translated} />
        </div>
      </section>

      <article lang={contentLang(translated)} className='mx-auto max-w-210 px-5 pt-12 pb-6 sm:px-8 sm:pt-14'>
        {description && <p className='text-ink mb-8 text-[22px] leading-[1.6]'>{description}</p>}
        <MarkdownDocs body={body} />
        {source?.url && (
          <a
            href={source.url}
            target='_blank'
            rel='noopener noreferrer'
            className='border-line mt-10 flex items-center justify-between gap-4 rounded-[18px] border px-6.5 py-5.5 transition-colors hover:border-brand'
          >
            <span>
              <span className='text-subtle block text-sm'>{m.article_original()}</span>
              <span className='font-bold'>{m.article_read_original({ source: source.name })}</span>
            </span>
            <ExternalLinkIcon className='size-5.5 shrink-0 text-brand' />
          </a>
        )}
      </article>

      {more.length > 0 && (
        <section className='mx-auto w-full max-w-310 px-5 pt-14 pb-16 sm:px-8 sm:pt-18 sm:pb-24'>
          <div className='mb-8 flex items-end justify-between gap-6'>
            <h2 className='text-[clamp(30px,3vw,42px)] leading-[1.12] font-bold tracking-[-0.015em]'>
              {m.article_more()}
            </h2>
            <Link to='/news' className='border-b border-current pb-0.5 font-semibold'>
              {m.article_all_news()}
            </Link>
          </div>
          <div className='grid grid-cols-[repeat(auto-fill,minmax(300px,1fr))] gap-5'>
            {more.map((item) => (
              <NewsCard key={item.slug} item={item} />
            ))}
          </div>
        </section>
      )}
    </>
  );
}
